import { Button, Input, Switch } from '@invent/wl-ui-kit-next/ui';
import { Pencil, Plus } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover,
  NotPermitted
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import { firmListsApi } from '../api/firm-lists-api';
import type {
  FirmRoute,
  IAdminTeamFirm,
  IFirmParticipants,
  IFirmRouteRegisterFirm,
  IFirmRouteRow
} from '../api/team-firm-contracts';
import { ROUTES_MAX_LIMIT, teamFirmsApi } from '../api/team-firms-api';
import { LoadError, useAdminData } from '../components/admin-page';
import { ParticipantLink } from '../components/participant-link';
import { refusalMessage } from '../refusal';
import { NoteLine, unanswered } from './person-hub/said';
import {
  NameChip,
  NamePicker,
  RouteChangeLine,
  type NameChoice,
  type NamePick,
  type ISpellingTarget,
  CHOOSE_TARGET,
  SEPARATE,
  SpellingTarget,
  targetOf,
  WIREHOUSE,
  SEPARATE_WARNING,
  addName,
  addSpelling,
  detachName,
  removeSpelling,
  spellingRefusal,
  isGone,
  mayHaveLanded,
  refusalText,
  refusedOn,
  useWriteNote
} from './team-firm-form';

/**
 * A firm's record on the Firms table (SR-513) — a dialog over the table, opened by a row's press
 * and addressed as `?firm=<crd>`, so it is linkable and Back closes it. It holds what the row
 * cannot: the name and its rename, the Wires switch, every register name mapped to the CRD — its
 * other names, with their spellings — taken off or added to, and the teams and people seen there.
 *
 * **The record reads its own row**, by its CRD, through the table's read (survey-api has no read of
 * one firm): the CRD searched, by CRD up, the row whose CRD it is — so a pasted address opens
 * whatever page the table is on. The row handed down from the table is drawn until it lands.
 *
 * **A rename is the wirehouse list's label**, so only a Wires firm is renamed here; a Gating firm's
 * name is its registry spelling or its first other name, said behind the "?". Every write is
 * `responses.annotate`, drawn off under one `NotPermitted` without it; each is said at the top of
 * the record, and a landed one reads the record and the page again.
 */
export function FirmRecordDialog({
  crd,
  initial,
  register,
  registerMissing,
  permitted,
  onClose,
  onChanged
}: {
  crd: number;
  /** The table's row for the CRD, where the page drawn holds it. */
  initial?: IFirmRouteRow;
  /** The register as the page last read it — the names to pick from. */
  register: Nullable<IAdminTeamFirm[]>;
  /** Why the register is not there to pick from, where it is not. */
  registerMissing?: string;
  permitted: boolean;
  onClose: () => void;
  /** A write landed or may have: the page reads its table and the register again. */
  onChanged: () => Promise<boolean>;
}) {
  const { data, error, reload } = useAdminData(
    async (signal) => ({ row: await readFirm(crd, signal) }),
    [crd]
  );
  const firm = data ? data.row : (initial ?? null);
  const [switching, setSwitching] = React.useState(false);
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;

  /** Everything this record and its page show, read again. */
  const rereadAll = async () => {
    const [own, page] = await Promise.all([reload(), onChanged()]);
    return own && page;
  };
  const { note, setNote, landed, lost } = useWriteNote(rereadAll, 'firm');
  const writes: IRecordWrites = {
    begin: () => setNote(null),
    landed: (text) => void landed(text),
    refused: (text) => setNote({ tone: 'refused', text }),
    lost: () => void lost(),
    reread: rereadAll,
    notPermitted
  };

  const name = firm ? nameOf(firm) : `CRD ${crd}`;
  return (
    <>
      <Dialog open onOpenChange={(next) => !next && onClose()}>
        <DialogContent
          className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto"
          data-firm-record={crd}
        >
          <DialogHeader>
            <DialogTitle className="break-words">{name}</DialogTitle>
            <DialogDescription>
              CRD {crd}
              {firm && ` · ${ROUTE_LABELS[firm.route]}`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {error && (
              <LoadError
                message={
                  firm ? `Could not refresh — showing the firm as last read. ${error}` : error
                }
              />
            )}
            {data && data.row === null && (
              <p className="text-sm text-muted-foreground">
                No firm with CRD {crd} is on the wirehouse list, seen in an answer or named on the
                register.
              </p>
            )}
            {!permitted && <NotPermitted id={reasonId} what="changing firms" />}
            {note && <NoteLine note={note} />}
            {firm && (
              <>
                <RecordSection title="Name">
                  <FirmName firm={firm} writes={writes} />
                </RecordSection>
                <RecordSection title="Route">
                  <span className="flex items-center gap-3 text-sm">
                    <WiresSwitch
                      firm={firm}
                      notPermitted={notPermitted}
                      onFlip={() => {
                        setNote(null);
                        setSwitching(true);
                      }}
                    />
                    {firm.route === 'wires' ? 'Wires — on the wirehouse list' : 'Gating'}
                  </span>
                </RecordSection>
                <RecordSection title="Other names">
                  <OtherNames
                    firm={firm}
                    register={register}
                    registerMissing={registerMissing}
                    writes={writes}
                  />
                </RecordSection>
              </>
            )}
            <SeenHere crd={crd} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {switching && firm && (
        <SwitchDialog
          firm={firm}
          onClose={() => setSwitching(false)}
          landed={landed}
          lost={lost}
        />
      )}
    </>
  );
}

/** One firm by its CRD, through the table's read — `null` where survey-api lists none. The CRD
 *  searched also matches other CRDs holding its digits and names holding them, so the window is
 *  ordered by CRD, up, and the row whose CRD it is is taken from it: every other CRD holding the
 *  digits is larger, and only rows matched by a name can sort before it — more than a window of
 *  them would hide it. */
async function readFirm(crd: number, signal?: AbortSignal): Promise<Nullable<IFirmRouteRow>> {
  const answer = await teamFirmsApi.routes(
    { route: 'all', q: String(crd), sort: 'crd', dir: 'asc', offset: 0, limit: ROUTES_MAX_LIMIT },
    signal
  );
  return answer.items.find((row) => row.finraFirmId === crd) ?? null;
}

/** What a record's writes tell it. */
interface IRecordWrites {
  begin: () => void;
  landed: (text: string) => void;
  refused: (text: string) => void;
  lost: () => void;
  /** The record and its page read again — after a refusal that found them out of date. */
  reread: () => Promise<boolean>;
  /** The id of the record's `NotPermitted`; present, every write is drawn off. */
  notPermitted?: string;
}

function RecordSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="grid gap-2">
      <h3 className="text-[0.8125rem] font-medium text-[var(--color-royal-blue)]">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The firm's name, and on Wires its rename — the member's `PUT` with the label. A `PUT` also puts a
 * firm on the list, so the firm is read again just before it: one taken off Wires since the record
 * was drawn is not renamed — that would put it back — and the record is read again. A refusal stays
 * on the form with the draft; an answer that never arrived closes it and re-reads.
 */
function FirmName({ firm, writes }: { firm: IFirmRouteRow; writes: IRecordWrites }) {
  const [draft, setDraft] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const errorId = React.useId();

  if (draft === null) {
    return (
      <div className="flex items-center gap-2">
        <span className="min-w-0 text-sm font-medium break-words">
          {firm.name ?? <span className="font-normal text-muted-foreground">No name yet</span>}
        </span>
        {firm.route === 'wires' ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 text-muted-foreground"
            disabled={writes.notPermitted !== undefined}
            aria-describedby={writes.notPermitted}
            onClick={() => {
              setRefused(null);
              setDraft(firm.name ?? '');
            }}
          >
            <Pencil className="size-3.5" aria-hidden />
            Rename
          </Button>
        ) : (
          <HelpPopover label="Why this name cannot be changed here">
            <p>
              A Gating firm is named by its BrokerCheck or IAPD spelling, or else by its first other
              name. Only a Wires firm carries a label of its own, so it is renamed here once Wires is
              on.
            </p>
          </HelpPopover>
        )}
      </div>
    );
  }

  const save = async () => {
    const label = draft.trim();
    setBusy(true);
    setRefused(null);
    writes.begin();
    try {
      let now: Nullable<IFirmRouteRow>;
      try {
        now = await readFirm(firm.finraFirmId);
      } catch (error) {
        setRefused(
          'Could not read the firm again, so nothing was sent — ' +
            `${refusalMessage(error, 'the request failed').replace(/\.$/, '')}. Try again.`
        );
        return;
      }
      if (now?.route !== 'wires') {
        setDraft(null);
        writes.refused(
          `${nameOf(firm)} is no longer on Wires, so it was not renamed: only a Wires firm takes ` +
            'a name of its own here.'
        );
        void writes.reread();
        return;
      }
      await firmListsApi.addMember(WIREHOUSE, firm.finraFirmId, label);
      setDraft(null);
      writes.landed(`${nameOf(firm)} is now named ${label} on the wirehouse list.`);
    } catch (error) {
      if (unanswered(error)) {
        setDraft(null);
        writes.lost();
      } else setRefused(refusalMessage(error, 'Could not rename this firm.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="grid max-w-md gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        aria-label={`Name of CRD ${firm.finraFirmId}`}
        aria-invalid={refused ? true : undefined}
        aria-describedby={refused ? errorId : undefined}
        disabled={busy}
        autoFocus
      />
      {refused && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {refused}
        </p>
      )}
      <span className="flex gap-1.5">
        <Button type="submit" size="sm" disabled={busy || draft.trim() === (firm.name ?? '')}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

/** What the Other names section asks to confirm before it is sent. */
type Pending =
  | { kind: 'remove-name'; name: IFirmRouteRegisterFirm }
  | { kind: 'remove-spelling'; name: IFirmRouteRegisterFirm; spelling: string }
  | { kind: 'add'; pick: NamePick; target: Nullable<string> };

/**
 * The register names mapped to the CRD — each with a × that takes the CRD off it (the name stays
 * on the register), and its spellings, each with a × and "Add a spelling" (a spelling joins the
 * same Rankings firm) — and "Add another name". For a firm with a register name a typed name is a
 * spelling: of the one name where there is one, and of the one the reader chooses where there are
 * several — nothing chosen for them, the choices in order of closeness to what was typed, and the
 * press waiting with the reason said; a separate register name is its own choice, warned of, since
 * Rankings counts it as another firm. For a firm with none, a register name gains the CRD or a new
 * one is made. Every change but a spelling typed in its own box is confirmed in place; each
 * spelling set is read afresh first.
 */
function OtherNames({
  firm,
  register,
  registerMissing,
  writes
}: {
  firm: IFirmRouteRow;
  register: Nullable<IAdminTeamFirm[]>;
  registerMissing?: string;
  writes: IRecordWrites;
}) {
  const missingId = React.useId();
  const [pending, setPending] = React.useState<Nullable<Pending>>(null);
  const [busy, setBusy] = React.useState(false);
  const crd = firm.finraFirmId;
  const held = new Set(firm.registerFirms.map((name) => name.code));
  const crdsOf = (code: string) =>
    (register?.find((name) => name.code === code)?.finraFirmIds ?? []).filter((id) => id !== crd);
  const names: ISpellingTarget[] = firm.registerFirms.map(({ code, label }) => ({ code, label }));
  const waiting = pending?.kind === 'add' && pending.pick.kind === 'typed' && pending.target === null;

  /** One write, said at the record's top; `refusal` words a refusal, the register's by default. */
  const run = async (
    write: () => Promise<string>,
    refusal: (error: unknown) => Promise<string> | string
  ): Promise<boolean> => {
    setBusy(true);
    writes.begin();
    try {
      writes.landed(await write());
      return true;
    } catch (error) {
      if (mayHaveLanded(error)) writes.lost();
      else {
        writes.refused(await refusal(error));
        // The name is gone: what the record drew is out of date.
        if (isGone(error)) void writes.reread();
      }
      return false;
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const off = writes.notPermitted !== undefined;
  const confirm = pending && (
    <Confirm
      busy={busy}
      press={pending.kind === 'add' ? (busy ? 'Adding…' : 'Add') : busy ? 'Removing…' : 'Remove'}
      warning={pending.kind === 'add' && pending.target === SEPARATE ? SEPARATE_WARNING : undefined}
      waiting={waiting ? CHOOSE_TARGET : undefined}
      extra={
        pending.kind === 'add' &&
        pending.pick.kind === 'typed' && (
          <SpellingTarget
            typed={pending.pick.label}
            names={names}
            value={pending.target}
            onChange={(target) => setPending({ ...pending, target })}
            disabled={busy}
          />
        )
      }
      onConfirm={() => {
        if (pending.kind === 'remove-name') {
          const { name } = pending;
          void run(async () => {
            await detachName(name, crd);
            return `“${name.label}” no longer names CRD ${crd}.`;
          }, (error) => refusalText(error, 'Could not remove this name.'));
        } else if (pending.kind === 'remove-spelling') {
          const { name, spelling } = pending;
          void run(async () => {
            await removeSpelling(name, spelling);
            return `“${spelling}” is no longer a spelling of “${name.label}”.`;
          }, (error) => refusalText(error, 'Could not remove this spelling.'));
        } else {
          const choice = choiceOf(pending.pick, pending.target, names);
          if (choice === null) return;
          void run(
            () => addName(choice, crd),
            (error) =>
              choice.kind === 'spelling'
                ? spellingRefusal(error, choice.label)
                : refusalText(error, 'Could not add this name.')
          );
        }
      }}
      onCancel={() => setPending(null)}
    >
      {pending.kind === 'remove-name'
        ? `Remove “${pending.name.label}” from this firm? ` +
          (crdsOf(pending.name.code).length > 0
            ? `It keeps naming CRD ${crdsOf(pending.name.code).join(', ')}.`
            : 'It stays on the register with no CRD, under Not matched yet.')
        : pending.kind === 'remove-spelling'
          ? `Remove the spelling “${pending.spelling}” of “${pending.name.label}”?`
          : addQuestion(pending.pick, crdsOf)}
    </Confirm>
  );

  return (
    <div className="grid gap-2">
      {firm.registerFirms.length === 0 ? (
        <p className="text-sm text-muted-foreground">None on the register.</p>
      ) : (
        <ul className="grid gap-2" aria-label="Other names">
          {firm.registerFirms.map((name) => (
            <li key={name.code} data-register-name={name.code} className="grid gap-1">
              <span>
                <NameChip
                  text={name.label}
                  remove={`Remove “${name.label}”`}
                  disabled={busy || off}
                  describedBy={writes.notPermitted}
                  onRemove={() => setPending({ kind: 'remove-name', name })}
                />
              </span>
              <Spellings
                name={name}
                busy={busy}
                writes={writes}
                onRemove={(spelling) => setPending({ kind: 'remove-spelling', name, spelling })}
                onAdd={(spelling) =>
                  run(
                    async () => {
                      await addSpelling(name, spelling);
                      return `“${spelling}” is now a spelling of “${name.label}”.`;
                    },
                    (error) => spellingRefusal(error, spelling)
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}

      {confirm}

      {!pending && (
        <span className="flex flex-wrap items-center gap-2">
          <NamePicker
            label="Add another name"
            trigger="Add another name"
            register={register ?? []}
            taken={held}
            named={names}
            disabled={busy || off || register === null}
            describedBy={
              [writes.notPermitted, register === null ? missingId : undefined]
                .filter(Boolean)
                .join(' ') || undefined
            }
            onPick={(pick) =>
              setPending({
                kind: 'add',
                pick,
                target: pick.kind === 'typed' ? targetOf(pick, names) : null
              })
            }
          />
          {register === null && (
            <span id={missingId} className="text-sm text-muted-foreground">
              {registerMissing ?? 'Reading the register…'}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/** A picked name as it is written, once a typed one has its choice — `null` while it waits. */
function choiceOf(
  pick: NamePick,
  target: Nullable<string>,
  names: readonly ISpellingTarget[]
): Nullable<NameChoice> {
  if (pick.kind !== 'typed') return pick;
  if (target === SEPARATE) return { kind: 'new', label: pick.label };
  const name = names.find((one) => one.code === target);
  return name ? { kind: 'spelling', label: pick.label, code: name.code, of: name.label } : null;
}

/** The question a picked name is confirmed by — a typed one's choice of what it spells under it. */
function addQuestion(choice: NamePick, crdsOf: (code: string) => number[]): string {
  if (choice.kind === 'typed') return `Add “${choice.label}” to this firm?`;
  if (choice.kind === 'new') return `Add “${choice.label}” as a new name of this firm?`;
  const others = crdsOf(choice.code);
  return (
    `Add “${choice.label}” to this firm?` +
    (others.length > 0 ? ` It also names CRD ${others.join(', ')}.` : '')
  );
}

/**
 * A register name's spellings, each with its × (the section confirms it), and "Add a spelling" —
 * a box of its own, sent as typed with no confirmation, the route-change sentence beside it; a
 * refusal keeps the draft, said at the record's top.
 */
function Spellings({
  name,
  busy,
  writes,
  onRemove,
  onAdd
}: {
  name: IFirmRouteRegisterFirm;
  busy: boolean;
  writes: IRecordWrites;
  onRemove: (spelling: string) => void;
  onAdd: (spelling: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = React.useState<Nullable<string>>(null);
  const off = writes.notPermitted !== undefined;
  return (
    <div className="flex flex-wrap items-center gap-1 pl-2.5">
      {name.aliases.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground">Also spelled</span>
          <ul className="contents" aria-label={`Spellings of ${name.label}`}>
            {name.aliases.map((spelling) => (
              <li key={spelling}>
                <NameChip
                  text={spelling}
                  remove={`Remove the spelling “${spelling}”`}
                  disabled={busy || off}
                  describedBy={writes.notPermitted}
                  onRemove={() => onRemove(spelling)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {draft === null ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-muted-foreground"
          aria-label={`Add a spelling of ${name.label}`}
          aria-describedby={writes.notPermitted}
          disabled={busy || off}
          onClick={() => setDraft('')}
        >
          <Plus className="size-3" aria-hidden />
          Add a spelling
        </Button>
      ) : (
        <form
          className="flex basis-full items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            void onAdd(draft.trim()).then((landed) => landed && setDraft(null));
          }}
        >
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label={`A spelling of ${name.label}`}
            disabled={busy}
            className="h-8 max-w-xs"
            autoFocus
          />
          <Button type="submit" size="sm" disabled={busy || draft.trim() === ''}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
            Cancel
          </Button>
        </form>
      )}
      {draft !== null && (
        <span className="basis-full">
          <RouteChangeLine />
        </span>
      )}
    </div>
  );
}

function Confirm({
  busy,
  press,
  extra,
  warning,
  waiting,
  onConfirm,
  onCancel,
  children
}: {
  busy: boolean;
  press: string;
  /** What is chosen before the press — a typed name's choice of what it spells. */
  extra?: React.ReactNode;
  /** Said apart, before the press — what the write does beyond the question. */
  warning?: string;
  /** Why the press waits, said beside it; present, it is drawn off. */
  waiting?: string;
  onConfirm: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  const reasonId = React.useId();
  return (
    <div role="group" aria-label="Confirm" className="grid gap-2 rounded-md border border-border p-3">
      <p className="text-sm">{children}</p>
      {extra}
      {warning && (
        <p role="note" className="text-sm font-medium text-amber-900">
          {warning}
        </p>
      )}
      {/* Every change confirmed here maps the CRD or changes a name it is known by. */}
      <RouteChangeLine />
      <span className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          disabled={busy || waiting !== undefined}
          aria-describedby={waiting !== undefined ? reasonId : undefined}
          onClick={onConfirm}
        >
          {press}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        {waiting !== undefined && (
          <span id={reasonId} className="text-sm text-muted-foreground">
            {waiting}
          </span>
        )}
      </span>
    </div>
  );
}

/** How many participants a page of "Teams and people seen here" holds. */
const SEEN_PAGE = 25;

/**
 * The teams and people whose latest answer names the firm as current employer — teams first —
 * each a link to its record page, a page at a time. The last page stays while the next is read.
 */
function SeenHere({ crd }: { crd: number }) {
  const [page, setPage] = React.useState(1);
  const { data: answer, error, reading, reload } = useAdminData<{
    page: number;
    read: IFirmParticipants;
  }>(
    async (signal) => ({
      page,
      read: await teamFirmsApi.participants(
        crd,
        { offset: (page - 1) * SEEN_PAGE, limit: SEEN_PAGE },
        signal
      )
    }),
    [crd, page]
  );
  // The page drawn is the one last read, which a failed read of the next leaves in place.
  const data = answer?.read ?? null;
  const shownPage = answer?.page ?? page;
  const total = data?.total ?? 0;
  const last = Math.max(1, Math.ceil(total / SEEN_PAGE));
  /** Another page — the one asked again where its read failed. */
  const go = (next: number) => (next === page ? void reload() : setPage(next));
  return (
    <RecordSection title={`Teams and people seen here${data ? ` (${total.toLocaleString('en-US')})` : ''}`}>
      {error && (
        <LoadError
          message={
            data === null
              ? error
              : `Could not refresh — showing the teams and people as last read. ${error}`
          }
        />
      )}
      {data === null ? (
        !error && <p className="text-sm text-muted-foreground">Reading…</p>
      ) : total === 0 ? (
        <p className="text-sm text-muted-foreground">
          No current team or person names this firm as their employer.
        </p>
      ) : (
        <>
          <ul
            aria-label="Teams and people seen here"
            aria-busy={reading || undefined}
            className="divide-y divide-border rounded-md border border-border text-sm"
          >
            {data.items.map((seen) => (
              <li
                key={seen.participantId}
                className="flex items-baseline justify-between gap-3 px-3 py-1.5"
              >
                <span className="flex min-w-0 items-baseline gap-2">
                  <ParticipantLink
                    participant={{ ...seen, recordType: seen.kind }}
                    className="truncate"
                  />
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {seen.kind === 'team' ? 'Team' : 'Person'}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  Seen {formatDateTime(seen.seenAt)}
                </span>
              </li>
            ))}
          </ul>
          {last > 1 && (
            <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {((shownPage - 1) * SEEN_PAGE + 1).toLocaleString('en-US')}–
                {((shownPage - 1) * SEEN_PAGE + data.items.length).toLocaleString('en-US')} of{' '}
                {total.toLocaleString('en-US')}
              </span>
              <span className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={shownPage <= 1 || reading}
                  onClick={() => go(shownPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={shownPage >= last || reading}
                  onClick={() => go(shownPage + 1)}
                >
                  Next
                </Button>
              </span>
            </span>
          )}
        </>
      )}
    </RecordSection>
  );
}

export const ROUTE_LABELS: Record<FirmRoute, string> = { wires: 'Wires', gating: 'Gating' };

function other(route: FirmRoute): FirmRoute {
  return route === 'wires' ? 'gating' : 'wires';
}

/** The name a firm is said by — or its CRD where nothing names it. */
export function nameOf(firm: IFirmRouteRow): string {
  return firm.name ?? `CRD ${firm.finraFirmId}`;
}

/** A firm's Wires switch: on the wirehouse list or off it; pressed, it asks to move the firm. */
export function WiresSwitch({
  firm,
  notPermitted,
  onFlip
}: {
  firm: IFirmRouteRow;
  /** The id of the `NotPermitted` sentence; present, the switch is drawn off. */
  notPermitted?: string;
  onFlip: () => void;
}) {
  return (
    <Switch
      checked={firm.route === 'wires'}
      onCheckedChange={onFlip}
      disabled={notPermitted !== undefined}
      aria-describedby={notPermitted}
      aria-label={`Wires for ${nameOf(firm)}`}
    />
  );
}

/** Said where a firm nothing names is refused a place on the wirehouse list. */
const NO_NAME_FOR_WIRES =
  'Nothing names this firm yet: give it another name first, or add it to Wires with a name ' +
  'through Add a firm.';

/**
 * The Wires switch, confirmed: the firm, where it goes, and what that does to a team there. A
 * refusal stays in the dialog; an answer that never arrived closes it and re-reads, since the move
 * may have landed.
 */
export function SwitchDialog({
  firm,
  onClose,
  landed,
  lost
}: {
  firm: IFirmRouteRow;
  onClose: () => void;
  /** The move landed: said by the page once it has read again. */
  landed: (text: string) => Promise<void>;
  /** The answer never arrived: the page reads again and says to check. */
  lost: () => Promise<void>;
}) {
  const target = other(firm.route);
  const name = nameOf(firm);
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);

  const move = async () => {
    setBusy(true);
    setRefused(null);
    try {
      // Sent bare: survey-api names the member by the registry's spelling, else the register
      // name the firm was added under — refused on `label` only where nothing names it.
      if (target === 'wires') await firmListsApi.addMember(WIREHOUSE, firm.finraFirmId);
      else await firmListsApi.removeMember(WIREHOUSE, firm.finraFirmId);
      onClose();
      void landed(`${name} moved to ${ROUTE_LABELS[target]}.`);
    } catch (error) {
      if (unanswered(error)) {
        onClose();
        void lost();
      } else if (refusedOn(error, 'label')) setRefused(NO_NAME_FOR_WIRES);
      else setRefused(refusalMessage(error, 'Could not move this firm.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move {name} to {ROUTE_LABELS[target]}?
          </DialogTitle>
          <DialogDescription>
            CRD {firm.finraFirmId}{' '}
            {target === 'wires' ? 'goes on the wirehouse list.' : 'comes off the wirehouse list.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm">
            {target === 'wires'
              ? 'Teams whose lead works there take the Wires route, and the survey’s wirehouse questions apply, from their next registration.'
              : 'Teams whose lead works there take the Gating route, and the survey’s wirehouse questions no longer apply, from their next registration.'}
          </p>
          {refused && (
            <p role="alert" className="text-sm text-destructive">
              {refused}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={busy} onClick={() => void move()}>
            {busy ? 'Moving…' : `Move to ${ROUTE_LABELS[target]}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
